export const FETCH_MAX_ATTEMPTS = 5;
export const FETCH_ATTEMPT_TIMEOUT_MS = 10000;
export const FETCH_RETRY_PAUSE_MS = 400;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function waitingMessage() {
  return 'Ожидание…';
}

export function connectionRetryMessage(attempt, maxAttempts) {
  return `Проблема с интернет-соединением. Попытка ${attempt} из ${maxAttempts}…`;
}

export function connectionFailedMessage() {
  return 'Не удалось подключиться к серверу. Проверьте интернет и попробуйте позже.';
}

function defaultShouldRetryResponse(response) {
  if (!response) return true;
  if (!response.ok) {
    return response.status >= 500 || response.status === 408 || response.status === 429;
  }
  return false;
}

/**
 * fetch с автоповторами при сетевых ошибках / таймауте / 5xx.
 * onRetryProgress({ attempt, maxAttempts, message, status })
 * status: waiting | retrying | completed | failed
 */
export async function fetchJsonWithRetry(requestUrl, init = {}, options = {}) {
  const maxAttempts = options.maxAttempts ?? FETCH_MAX_ATTEMPTS;
  const attemptTimeoutMs = options.attemptTimeoutMs ?? FETCH_ATTEMPT_TIMEOUT_MS;
  const pauseMs = options.pauseMs ?? FETCH_RETRY_PAUSE_MS;
  const onRetryProgress = options.onRetryProgress || null;
  const shouldRetryResponse = options.shouldRetryResponse || defaultShouldRetryResponse;

  let lastError = null;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    if (onRetryProgress) {
      onRetryProgress({
        attempt,
        maxAttempts,
        message: waitingMessage(),
        status: 'waiting',
      });
    }

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), attemptTimeoutMs);

    let attemptFailed = false;

    try {
      const response = await fetch(requestUrl, {
        ...init,
        signal: controller.signal,
      });
      clearTimeout(timer);

      const raw = await response.text();
      let data = null;
      if (raw) {
        try {
          data = JSON.parse(raw);
        } catch (e) {
          data = raw;
        }
      }

      const retryHttp = shouldRetryResponse(response, data, null);
      if (!retryHttp) {
        if (onRetryProgress) {
          onRetryProgress({ attempt, maxAttempts, message: null, status: 'completed' });
        }
        return { response, data, raw, attempt };
      }

      attemptFailed = true;
      lastError = new Error(
        (typeof data === 'object' && data?.error) ? String(data.error) : `HTTP ${response.status}`
      );
    } catch (err) {
      clearTimeout(timer);
      lastError = err;
      const retryNetwork = shouldRetryResponse(null, null, err);
      if (!retryNetwork) {
        break;
      }
      attemptFailed = true;
    }

    if (attemptFailed && attempt < maxAttempts) {
      if (onRetryProgress) {
        onRetryProgress({
          attempt,
          maxAttempts,
          message: connectionRetryMessage(attempt, maxAttempts),
          status: 'retrying',
        });
      }
      await sleep(pauseMs);
    } else if (attemptFailed && attempt >= maxAttempts) {
      if (onRetryProgress) {
        onRetryProgress({
          attempt,
          maxAttempts,
          message: connectionRetryMessage(attempt, maxAttempts),
          status: 'retrying',
        });
      }
    }
  }

  const failMsg = connectionFailedMessage();
  if (onRetryProgress) {
    onRetryProgress({
      attempt: maxAttempts,
      maxAttempts,
      message: failMsg,
      status: 'failed',
    });
  }
  const error = lastError || new Error(failMsg);
  error.userMessage = failMsg;
  throw error;
}

export function loginShouldRetry(response, data, err) {
  if (err) return true;
  if (!response) return true;
  if (response.ok && data?.error) return false;
  if (response.ok) return false;
  return response.status >= 500 || response.status === 408 || response.status === 429;
}
